import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { relatedPostsQueryOptions } from "@/lib/public-content.queries";
import { retryImageOnce } from "@/lib/storage-image";

const formatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

export function RelatedPosts({
  category,
  excludeSlug,
}: {
  category: string;
  excludeSlug: string;
}) {
  const { data } = useQuery(relatedPostsQueryOptions(category, excludeSlug));
  const posts = data ?? [];
  if (posts.length === 0) return null;
  return (
    <section
      aria-labelledby="related-headline"
      className="border-t border-border bg-background"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-12 section-y">
        <h2 id="related-headline" className="heading-h2">
          Weiterlesen
        </h2>
        <ul className="mt-10 grid gap-10 md:grid-cols-3">
          {posts.map((post) => {
            const date = post.published_at ?? post.created_at;
            return (
              <li key={post.id}>
                <article className="group flex h-full flex-col">
                  <Link
                    to="/blog/$slug"
                    params={{ slug: post.slug }}
                    className="mb-4 block aspect-[16/10] w-full overflow-hidden bg-gradient-to-br from-secondary to-accent"
                  >
                    {post.cover_url ? (
                      <img
                        src={post.cover_url}
                        onError={retryImageOnce}
                        alt={post.title}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : null}
                  </Link>
                  <p className="text-xs text-muted-foreground">
                    {post.category} · {formatter.format(new Date(date))}
                  </p>
                  <h3 className="heading-h3 mt-2">
                    <Link
                      to="/blog/$slug"
                      params={{ slug: post.slug }}
                      className="transition-opacity hover:opacity-70"
                    >
                      {post.title}
                    </Link>
                  </h3>
                  {post.excerpt && (
                    <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                      {post.excerpt}
                    </p>
                  )}
                </article>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
